import DataAdapterOptionsView from "./DataAdapterOptionsView";
import {ModeltypeBasicDto} from "../../services/Client";
import {OptionBatchDeleteRequest} from "../../services/OptionBatchDeleteDtos";
import {OptionSelectionRequest} from "../../services/OptionSelectionDtos";
import {OptionAssignmentTransferRequest} from "../../services/OptionTransferDtos";

export interface INewGridSelection {
    selectAllMode: boolean;
    selectedIds: Set<number>;
    selectedDtos: Map<number, DataAdapterOptionsView>;
    excludedIds: Set<number>;
}

export type HeaderSelectionState = 'none' | 'some' | 'all';

export const emptySelection = (): INewGridSelection => ({
    selectAllMode: false,
    selectedIds: new Set<number>(),
    selectedDtos: new Map<number, DataAdapterOptionsView>(),
    excludedIds: new Set<number>()
});

export const clearSelection = emptySelection;

export const selectAll = (): INewGridSelection => ({
    selectAllMode: true,
    selectedIds: new Set<number>(),
    selectedDtos: new Map<number, DataAdapterOptionsView>(),
    excludedIds: new Set<number>()
});

export const isRowSelected = (selection: INewGridSelection, objectId: number): boolean =>
    selection.selectAllMode
        ? !selection.excludedIds.has(objectId)
        : selection.selectedIds.has(objectId);

export const toggleRow = (
    selection: INewGridSelection,
    objectId: number,
    checked: boolean,
    dto?: DataAdapterOptionsView
): INewGridSelection => {
    if (selection.selectAllMode) {
        const excludedIds = new Set<number>(selection.excludedIds);
        if (checked) {
            excludedIds.delete(objectId);
        } else {
            excludedIds.add(objectId);
        }
        return {...selection, excludedIds};
    }

    const selectedIds = new Set<number>(selection.selectedIds);
    const selectedDtos = new Map<number, DataAdapterOptionsView>(selection.selectedDtos);
    if (checked) {
        selectedIds.add(objectId);
        if (dto) {
            selectedDtos.set(objectId, dto);
        }
    } else {
        selectedIds.delete(objectId);
        selectedDtos.delete(objectId);
    }
    return {...selection, selectedIds, selectedDtos};
};

export const selectedCount = (selection: INewGridSelection, filterCount: number): number =>
    selection.selectAllMode
        ? Math.max(0, (filterCount || 0) - selection.excludedIds.size)
        : selection.selectedIds.size;

export const hasSelection = (selection: INewGridSelection, filterCount: number): boolean =>
    selectedCount(selection, filterCount) > 0;

export const headerState = (selection: INewGridSelection, filterCount: number): HeaderSelectionState => {
    if (!filterCount || filterCount <= 0) {
        return 'none';
    }
    const count = selectedCount(selection, filterCount);
    if (count <= 0) {
        return 'none';
    }
    return count >= filterCount ? 'all' : 'some';
};

export const getSelectedDtos = (selection: INewGridSelection): DataAdapterOptionsView[] =>
    selection.selectAllMode ? [] : Array.from(selection.selectedDtos.values());

export const getSelectedIds = (selection: INewGridSelection): number[] =>
    selection.selectAllMode ? [] : Array.from(selection.selectedIds.values());

export const getExcludedIds = (selection: INewGridSelection): number[] =>
    Array.from(selection.excludedIds.values());

export interface IPslContextIds {
    modelyearId: number;
    typefamilyId: number;
    cnrId: number;
}

export interface ISelectionRequestOverrides {
    includedIds?: number[];
    additionalExcludedIds?: number[];
}

const uniqueIds = (ids: number[]): number[] => Array.from(new Set<number>(ids).values());

export const buildSelectionRequest = (
    selection: INewGridSelection,
    context: IPslContextIds,
    filters: any,
    overrides: ISelectionRequestOverrides = {}
): OptionSelectionRequest => {
    const includedIds = selection.selectAllMode
        ? []
        : (overrides.includedIds !== undefined ? overrides.includedIds.slice() : getSelectedIds(selection));
    const excludedIds = selection.selectAllMode
        ? uniqueIds(getExcludedIds(selection).concat(overrides.additionalExcludedIds || []))
        : [];
    return {
        modelyearId: context.modelyearId,
        typefamilyId: context.typefamilyId,
        cnrId: context.cnrId,
        selectAllMatchingFilter: selection.selectAllMode,
        filters: {...(filters || {})},
        includedIds,
        excludedIds
    };
};

export const buildBatchDeleteRequest = (
    selection: INewGridSelection,
    context: IPslContextIds,
    filters: any,
    includedIdsOverride?: number[]
): OptionBatchDeleteRequest =>
    buildSelectionRequest(selection, context, filters, {includedIds: includedIdsOverride});

/**
 * Request for `PATCH /v2/options/transfer-assignments`: the selection (see `buildSelectionRequest`) plus the
 * source / target modeltypes chosen in the transfer dialog.
 */
export const buildTransferRequest = (
    selection: INewGridSelection,
    context: IPslContextIds,
    filters: any,
    sourceMTB: ModeltypeBasicDto,
    targetMTBs: ModeltypeBasicDto[],
    overrides: ISelectionRequestOverrides = {}
): OptionAssignmentTransferRequest => ({
    ...buildSelectionRequest(selection, context, filters, overrides),
    sourceMTB,
    targetMTBs: targetMTBs.slice()
});



// WEBPACK FOOTER //
// ./src/views/OptionsView/newGridSelection.ts